import React, { useMemo, useCallback } from 'react';
import { USER_GROUP_LIST_QUERY } from '../queries';
import GroupIcon from '@mui/icons-material/Group';
import { Card } from '@totalsoft/rocket-ui';
import { ExportModalComponent } from 'components/common/export/ExportModalComponent';
import TableListBrowser from 'features/common/components/table-list-browser/TableListBrowser';
import UserGroupListComponent from './UserGroupListComponent';
import ApolloFetchingComponent from 'features/common/components/apollo-fetching/ApolloFetchingComponent';
import userGroupStore from '../userGroupStore';
import { useMutation } from 'hooks/apollo/useMutation';
import { DELETE_USER_GROUP } from '../mutations';
import useConfirmDialog from 'features/common/components/confirm/useConfirmDialog';
import { USER_GROUP_DELETED } from '../subscriptions';
import { useToast } from 'hooks/toasts/useToast';
import { useTranslation } from 'react-i18next';
import useSubscription from 'hooks/apollo/useSubscription';

const UserGroupListBrowser = () => {
  const addToast = useToast();
  const confirm = useConfirmDialog();
  const { t } = useTranslation();

  const fetchingOptions = useMemo(
    () => ({
      queryOptions: {
        query: USER_GROUP_LIST_QUERY,
        name: 'userGroupList'
      }
    }),
    []
  );

  const columns = useMemo(
    () => [
      { name: 'code', caption: 'UserGroups.Code' },
      { name: 'name', caption: 'UserGroups.Name' },
      { name: 'actions', caption: 'Actions', width: 15 }
    ],
    []
  );

  // #region Delete

  const [deleteUserGroup] = useMutation(DELETE_USER_GROUP);

  const handleDelete = useCallback(
    userGroupId => {
      confirm(
        () => {
          deleteUserGroup({ variables: { userGroupId } });
        },
        {
          title: t('UserGroups.DeleteUserGroup'),
          content: t('UserGroups.AreYouSureDeleteUserGroup')
        }
      );
    },
    [confirm, t, deleteUserGroup]
  );

  useSubscription(USER_GROUP_DELETED, {
    onData: ({ client }) => {
      addToast(t('UserGroups.UserGroupDeleted'), 'success');
      client.cache.evict({ id: 'ROOT_QUERY', fieldName: 'userGroupList' });
      client.cache.gc();
    }
  });

  // #endregion

  const itemComponent = useCallback(({ data }) => <UserGroupListComponent data={data} onDelete={handleDelete} />, [handleDelete]);

  return (
    <Card icon={GroupIcon}>
      <TableListBrowser name='user-groups-browser' store={userGroupStore} ItemComponent={itemComponent} columns={columns} />
      <ExportModalComponent store={userGroupStore} options={fetchingOptions} />
      <ApolloFetchingComponent store={userGroupStore} options={fetchingOptions} />
    </Card>
  );
};

export default UserGroupListBrowser;
